import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Save } from 'lucide-react';
import { AssetField, AssetSelect } from './AssetFields';
import { categories, statuses, currencies, emptyAsset, assetPayload } from './assetUtils';

export const AssetFormDialog = ({ asset, onClose, onSave }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...emptyAsset, ...asset });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const rental = ['available_for_rent', 'rented_out'].includes(form.status);
  const rented = form.status === 'rented_out';
  const options = (values, group) => values.map(value => ({ value, label: group ? t(`assets.${group}.${value}`) : value }));
  const field = (name, props = {}) => <AssetField field={name} form={form} setForm={setForm} {...props} />;
  const submit = async e => {
    e.preventDefault(); setError('');
    if (!form.name.trim()) { setError(t('assets.nameRequired')); return; }
    if (rented && (!form.tenant_name.trim() || !form.lease_start || Number(form.monthly_rent) <= 0)) { setError(t('assets.rentalRequired')); return; }
    if (rented && form.lease_end && form.lease_end < form.lease_start) { setError(t('assets.invalidDates')); return; }
    setSaving(true);
    try { await onSave(assetPayload(form), asset?.id); onClose(); }
    catch { setError(t('assets.saveFailed')); }
    finally { setSaving(false); }
  };
  return <Dialog open onOpenChange={open => { if (!open && !saving) onClose(); }}>
    <DialogContent data-testid="asset-form-dialog" closeLabel={t('assets.close')} aria-describedby={undefined} className="w-[calc(100%_-_2rem)] max-w-2xl">
      <DialogHeader><DialogTitle data-testid="asset-form-title">{t(asset?.id ? 'assets.editAsset' : 'assets.addAsset')}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-6">
        <fieldset disabled={saving} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('name', { required: true, maxLength: 160 })}{field('reference', { maxLength: 80 })}
            <AssetSelect id="asset-category-select" label={t('assets.fields.category')} value={form.category} options={options(categories, 'categories')} onChange={category => setForm(prev => ({ ...prev, category }))} />
            <AssetSelect id="asset-status-select" label={t('assets.fields.status')} value={form.status} options={options(statuses, 'statuses')} onChange={status => setForm(prev => ({ ...prev, status }))} />
            {field('location', { maxLength: 300 })}{field('owner_name', { maxLength: 160 })}
            {field('area_m2', { type: 'number', min: 0, step: '0.01', max: 1e10 })}{field('acquisition_date', { type: 'date' })}
            {field('estimated_value', { type: 'number', min: 0, step: '0.01', max: 1e12 })}
            <AssetSelect id="asset-currency-select" label={t('assets.fields.currency')} value={form.currency} options={options(currencies)} onChange={currency => setForm(prev => ({ ...prev, currency }))} />
          </div>
          {rental && <section className="border-t border-slate-200 pt-5 space-y-4" data-testid="asset-rental-fields">
            <h2 className="font-semibold text-base">{t('assets.rentalDetails')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field('monthly_rent', { type: 'number', min: rented ? 0.01 : 0, step: '0.01', max: 1e10, required: rented })}
              {rented && <>{field('tenant_name', { required: true, maxLength: 160 })}{field('tenant_contact', { maxLength: 250 })}
                {field('lease_start', { type: 'date', required: true })}{field('lease_end', { type: 'date', min: form.lease_start || undefined })}</>}
            </div>
          </section>}
          <div className="space-y-1.5"><Label htmlFor="asset-notes">{t('assets.fields.notes')}</Label><Textarea id="asset-notes" data-testid="asset-notes-input" maxLength={5000} value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} /></div>
        </fieldset>
        {error && <p role="alert" className="text-sm text-red-600" data-testid="asset-form-error">{error}</p>}
        <div className="flex justify-end gap-3 border-t pt-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="asset-cancel-button">{t('assets.cancel')}</Button>
          <Button type="submit" disabled={saving} data-testid="asset-save-button" className="bg-indigo-600 hover:bg-indigo-700">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{t('assets.save')}</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>;
};